import React, { useState, useEffect, useCallback } from 'react';
import { registrationRequest } from '../remote/authRequest';
import {
  Typography,
  Container,
  Box,
  Paper,
  Snackbar,
  Alert,
  Divider,
  Button
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import PurpleButton from '../components/LayoutComponents/PurpleButton';
import theme from '../components/LayoutComponents/theme';
import Logo from '../components/LayoutComponents/Logo';
import Background from '../components/LayoutComponents/Background';
import { passwordRegex } from '../staticDataSet';
import UserForm from './Member/UserForm';
import { reCaptcha } from '../utils/reCaptcha';
import LoadingCircle from '../components/LayoutComponents/LoadingCircle';
import PostRegistrationCard from '../components/LayoutComponents/PostRegistrationCard';

export default function RegistrationPage({ isMobile }) {
  const initialFormData = {
    country: 'United States',
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    primaryPhone: '',
    alternatePhone: '',
    militaryService: false,
    militaryServiceBranch: '',
    militaryServiceStartDate: null,
    militaryServiceEndDate: null,
    militaryServiceStatus: '',
    refferedBy: '',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [captchaValue, setCaptchaValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const navigate = useNavigate();
  const handleClick = () => navigate('/');

  /** Refresh ReCAPTCHA **/
  const refreshCaptcha = useCallback(() => {
    reCaptcha('register', (token) => setCaptchaValue(token));
  }, []);

  useEffect(() => {
    if (!captchaValue) refreshCaptcha();
  }, [captchaValue, refreshCaptcha]);

  /** Password Validation **/
  const isPasswordValid = (password) => passwordRegex.test(password);

  /** Form Validation **/
  const validateForm = () => {
    const {
      username,
      password,
      confirmPassword,
      firstName,
      lastName,
      primaryPhone,
      addressLine1,
      city,
      state,
      zipCode,
      militaryService,
      militaryServiceBranch,
      militaryServiceStartDate,
      militaryServiceEndDate,
      militaryServiceStatus,
      refferedBy,
    } = formData;

    const newErrors = {};

    if (!username) newErrors.username = 'Username is required.';
    if (!password) newErrors.password = 'Password is required.';
    if (!confirmPassword) newErrors.confirmPassword = 'Confirm Password is required.';
    if (password && password.length > 16) newErrors.password = 'Password cannot exceed 16 characters.';
    if (!isPasswordValid(password)) newErrors.password =
      'Password must be 8–16 characters long, with one uppercase and one special character.';
    if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match.';

    if (!firstName) newErrors.firstName = 'First Name is required.';
    if (!lastName) newErrors.lastName = 'Last Name is required.';
    if (!primaryPhone) newErrors.primaryPhone = 'Primary Phone is required.';
    if (!addressLine1) newErrors.addressLine1 = 'Address is required.';
    if (!city) newErrors.city = 'City is required.';
    if (!state) newErrors.state = 'State is required.';
    if (!zipCode) newErrors.zipCode = 'Zip Code is required.';

    if (militaryService) {
      if (!militaryServiceBranch) newErrors.militaryServiceBranch = 'Branch is required.';
      if (!militaryServiceStartDate) newErrors.militaryServiceStartDate = 'Start Date is required.';
      if (!militaryServiceEndDate) newErrors.militaryServiceEndDate = 'End Date is required.';
      if (!militaryServiceStatus) newErrors.militaryServiceStatus = 'Status is required.';
      if (militaryServiceStartDate && militaryServiceEndDate) {
        if (new Date(militaryServiceStartDate) > new Date(militaryServiceEndDate))
          newErrors.militaryServiceEndDate = 'End Date must be after Start Date.';
      }
      if (refferedBy && refferedBy.length > 50) newErrors.refferedBy = 'Referred By cannot exceed 50 characters.';
    }

    return newErrors;
  };

  /** Handle Submit **/
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    setRegistrationComplete(true);
    try {
      await registrationRequest(
        {
          id: 0,
          ...formData,
          addressLine1: formData.addressLine1.split(',')[0],
        },
        captchaValue
      );

      setFormData(initialFormData);
      setCaptchaValue(null);
      setErrors({});
    } catch (error) {
      setRegistrationComplete(false);
      const message =
        error?.response?.data?.message ||
        '❌ An unexpected error occurred. Please try again.';
      setFeedback({ open: true, message, severity: 'error' });
    } finally {
      setLoading(false);
      refreshCaptcha();
    }
  };

  /** UI **/
  return (
    <Background>
      <Container component="main" maxWidth="md" sx={{ py: 6 }}>
        {/* Show either the Form or the Success Card */}
        {registrationComplete ? (
          <PostRegistrationCard loading={loading} />
        ) : (
          <Paper elevation={4} sx={{ p: isMobile ? 3 : 6, borderRadius: 3, backgroundColor: '#eef1ff' }}>
            {/* Logo */}
            <Box display="flex" justifyContent="center" mb={3}>
              <Logo style={{ width: isMobile ? '280px' : '420px' }} />
            </Box>

            {/* Intro Header */}
            <Typography variant="h4" fontWeight="bold" color={theme.colors.yellow} align="center" gutterBottom>
              Welcome to the National Talent Hub™
            </Typography>

            <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 4 }}>
              Connecting industry, talent, and training for the future of U.S. manufacturing and technology.
            </Typography>

            {/* Intro Content */}
            <Box sx={{ maxWidth: 700, mx: 'auto', mb: 4 }}>
              <Typography variant="body2" paragraph>
                By registering, you’ll gain access to career and apprenticeship opportunities that match your skills and interests — helping you explore clear, skill-based career pathways in advanced manufacturing, semiconductors, and other high-growth sectors.
              </Typography>
              <Typography variant="body2" paragraph>
                When you create your profile, you also join <span style={{ color: theme.colors.purple }}>NIICA’s National Apprentice Network</span>, a nationwide community of employers, educators, and workforce partners building the next generation of skilled professionals.
              </Typography>
            </Box>
            <Divider sx={{ mb: 4 }} />
            <Box
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 3 }}
            >
              <Typography>Already a member?</Typography>
              <Button variant='none' size="small" onClick={handleClick} >
                <Typography
                  variant="h6"
                  sx={{ color: 'red', fontWeight: theme.typography.fontWeightBold}}
                  >
                    Sign In
                  </Typography>
              </Button>
            </Box>
            <Divider sx={{ mb: 4 }} />

            {/* Registration Form */}
            <>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <LoadingCircle />
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom color={theme.colors.purple}>
                  Create Your Account
                </Typography>
              </Box>
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <UserForm formData={formData} setFormData={setFormData} formType="registration" error={errors} />

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" align="center" color="textSecondary">
                    By signing up, you agree to our Terms of Service and{' '}
                    <Link to="/compliance" style={{ color: theme.colors.purple, textDecoration: 'none' }}>
                      Privacy Policy
                    </Link>.
                  </Typography>
                  <PurpleButton
                    type="submit"
                    label="Create Your Account"
                    size="large"
                    color="green"
                    disabled={!captchaValue || loading}
                    sx={{ width: isMobile ? '100%' : 220 }}
                  />
                </Box>
              </Box>
            </>
          </Paper>)}
      </Container>

      {/* Snackbar Feedback */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={feedback.severity} sx={{ width: '100%' }}>
          {feedback.message}
        </Alert>
      </Snackbar>
    </Background>
  );
}
